<template>
  <div class="app-container" :class="{ 'dark-theme': isDarkTheme }">
    <!-- Контейнер -->
    <div class="tg-app-container">
      <!-- Кнопка смены темы в верхнем левом углу -->
      <button 
        @click="toggleTheme" 
        class="theme-toggle-sketch"
        :title="isDarkTheme ? 'Переключить на светлую тему' : 'Переключить на темную тему'"
      >
        <div class="toggle-container">
          <!-- Солнце -->
          <div class="sun-icon" :class="{ active: !isDarkTheme }">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="5"/>
              <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
            </svg>
          </div>
          
          <!-- Разделитель -->
          <div class="toggle-divider">/</div>
          
          <!-- Луна -->
          <div class="moon-icon" :class="{ active: isDarkTheme }">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          </div>
        </div>
        
        <!-- Рукописная подпись -->
        <div class="toggle-label">
          {{ isDarkTheme ? 'темная' : 'светлая' }}
        </div>
        
        <!-- Декоративная стрелка -->
        <svg class="toggle-arrow" viewBox="0 0 20 15" fill="none" stroke="currentColor">
          <path d="M2,7 Q8,3 14,7" stroke-width="1.5"/>
          <path d="M11,4 L14,7 L11,10" stroke-width="1.5"/>
        </svg>
      </button>

      <!-- Шапка в стиле скетча -->
      <header class="header">
        <div class="header-sketch">
          <!-- Рукописный заголовок -->
          <div class="handwritten-title">
            <span class="month-badge">ПОТОК</span>
            <h1 class="main-title">Учебная платформа</h1>
            <div class="subtitle-arrow">
              <svg class="arrow-sketch" viewBox="0 0 100 30" fill="none" stroke="currentColor">
                <path d="M10,15 Q30,5 50,15 T90,15" stroke-width="2" fill="none"/>
                <path d="M85,10 L90,15 L85,20" stroke-width="2" fill="none"/>
              </svg>
              <p class="subtitle-text">Курсы, статьи и видеоуроки для саморазвития</p>
            </div>
          </div>
          
          <!-- Декоративные элементы -->
          <div class="sketch-elements">
            <div class="brain-sketch">
              <svg viewBox="0 0 50 40" fill="none" stroke="currentColor">
                <path d="M15,20 Q10,10 20,8 Q30,5 35,10 Q45,8 40,18 Q45,25 35,30 Q25,35 15,30 Q5,25 15,20" stroke-width="1.5"/>
                <circle cx="20" cy="15" r="1" fill="currentColor"/>
                <circle cx="30" cy="18" r="1" fill="currentColor"/>
              </svg>
            </div>
            <div class="lightbulb-sketch">
              <svg viewBox="0 0 30 40" fill="none" stroke="currentColor">
                <circle cx="15" cy="15" r="8" stroke-width="1.5"/>
                <path d="M15,23 L15,30" stroke-width="2"/>
                <path d="M12,30 L18,30" stroke-width="2"/>
                <path d="M10,8 Q15,3 20,8" stroke-width="1"/>
              </svg>
            </div>
          </div>
        </div>
      </header>

      <!-- Поиск в рукописном стиле -->
      <div v-if="currentPage === 'explore'" class="search-container">
        <div class="search-sketch-wrapper">
          <div class="search-label">Что ищем?</div>
          <div class="search-input-wrapper">
            <input 
              v-model="searchQuery"
              type="text" 
              placeholder="Поиск материалов..."
              class="search-input-sketch"
            >
            <div class="search-underline"></div>
          </div>
        </div>
      </div>

      <!-- Разделы в стиле коллажа (только для главной) -->
      <div v-if="currentPage === 'explore'" class="categories-collage">
        <div class="collage-title">
          <span class="handwritten-text">Выберите раздел:</span>
          <svg class="underline-sketch" viewBox="0 0 200 10" fill="none" stroke="currentColor">
            <path d="M5,5 Q50,2 100,5 T195,5" stroke-width="1.5"/>
          </svg>
        </div>
        
        <div class="categories-grid-sketch">
          <div 
            v-for="(category, index) in categories" 
            :key="category.id"
            @click="selectedCategory = category"
            :class="['category-card-sketch', selectedCategory === category.name ? 'category-active-sketch' : '']"
            :style="{ transform: `rotate(${getRandomRotation(index)}deg)` }"
          >
            <!-- Рамка в стиле скетча -->
            <div class="sketch-frame">
              <svg class="frame-border" viewBox="0 0 200 150" fill="none" stroke="currentColor">
                <path d="M10,10 L190,12 L188,140 L12,138 Z" stroke-width="2" stroke-dasharray="5,3"/>
              </svg>
            </div>
            
            <div class="category-content-sketch">
              <div class="category-icon-sketch">
                <svg class="category-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="category.icon" />
                </svg>
              </div>
              <h3 class="category-name-handwritten">{{ category.name }}</h3>
              
              <!-- Прогресс в виде рукописной полоски -->
              <div class="progress-sketch">
                <div class="progress-label">{{ getCategoryProgress(category.text) }}% пройдено</div>
                <svg class="progress-line" viewBox="0 0 100 8" fill="none">
                  <path d="M5,4 L95,4" stroke="#ddd" stroke-width="2"/>
                  <path :d="`M5,4 L${5 + getCategoryProgress(category.text) * 0.9},4`" stroke="currentColor" stroke-width="3"/>
                </svg>
              </div>
            </div>
            
            <!-- Стикер с номером -->
            <div class="category-sticker">{{ category.text === 'all' ? materials.length : materials.filter(material => material.categories.includes(category.text)).length }}</div>
          </div>
        </div>
      </div>

      <!-- Фильтры в стиле заметок -->
      <div v-if="currentPage === 'explore'" class="tags-notes">
        <div class="notes-title">Фильтры:</div>
        <div class="tag-notes-grid">
          <button 
            v-for="tag in tags" 
            :key="tag.id"
            @click="selectedTag = tag.id"
            :class="['tag-note', selectedTag === tag.id ? 'tag-note-active' : '']"
          >
            <div class="note-corner"></div>
            {{ tag.name }}
          </button>
        </div>
      </div>

      <!-- Главная страница - список материалов в стиле журнала -->
      <div v-if="currentPage === 'explore'" class="materials-magazine">
        <div 
          v-for="(material, index) in filteredMaterials" 
          :key="material.id"
          class="material-article"
          @click="selectMaterial(material)"
          :style="{ transform: `rotate(${getRandomRotation(index, 1)}deg)` }"
        >
          <!-- Рамка статьи -->
          <div class="article-frame">
            <svg class="article-border" viewBox="0 0 300 200" fill="none" stroke="currentColor">
              <rect x="5" y="5" width="290" height="190" stroke-width="1.5" stroke-dasharray="8,4" rx="3"/>
            </svg>
          </div>
          
          <div class="article-content">
            <!-- Заголовок статьи -->
            <div class="article-header">
              <div class="article-type-badge" :class="getTypeClass(material.type)">
                {{ material.type }}
              </div>
              <h3 class="article-title" v-html="parseMarkdownV2(material.text.split('\n')[0].length <=75 ? material.text.split('\n')[0] : material.text.slice(0, 72) + '...')"></h3>
              
              <!-- Кнопка избранного в виде скрепки -->
              <button 
                @click.stop="toggleFavorite(material.id)"
                class="paperclip-favorite"
                :class="material.isFavorite ? 'paperclip-active' : ''"
              >
                <svg viewBox="0 0 20 30" fill="none" stroke="currentColor">
                  <path d="M6,8 L6,20 Q6,24 10,24 Q14,24 14,20 L14,6 Q14,4 12,4 Q10,4 10,6 L10,18" stroke-width="2"/>
                </svg>
              </button>
            </div>
            
            <p class="article-description" v-html="parseMarkdownV2(material.text.slice(0, 100) + (material.text.length > 100 ? '...' : ''))"></p>
            
            <!-- Статистика в рукописном стиле -->
            <div class="article-stats-sketch">
              <div class="stat-sketch">
                <span class="stat-icon">📅</span>
                <span>{{ getDate(material.created_at) }}</span>
              </div>
              <div class="stat-sketch" :style="{ display: material.rating ? 'block' : 'none' }">
                <span class="stat-icon">⭐</span>
                <span>{{ material.rating }}</span>  
              </div>
              
              <!-- Чекбокс выполнения -->
              <button 
                @click.stop="toggleCompleted(material.id)" 
                class="checkbox-sketch"
                :class="material.completed ? 'checkbox-checked' : ''"
              >
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor">
                  <rect x="2" y="2" width="16" height="16" stroke-width="2" rx="2"/>
                  <path v-if="material.completed" d="M6,10 L9,13 L14,7" stroke-width="2"/>
                </svg>
                <span>{{ material.completed ? 'Готово!' : 'Отметить' }}</span>
              </button>
            </div>
          </div>
          
          <!-- Декоративная стрелка -->
          <div class="article-arrow" v-if="index % 3 === 0">
            <svg viewBox="0 0 50 30" fill="none" stroke="currentColor">
              <path d="M5,15 Q20,5 35,15" stroke-width="1.5"/>
              <path d="M30,10 L35,15 L30,20" stroke-width="1.5"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- Страница избранного в стиле доски объявлений -->
      <div v-if="currentPage === 'favorites'" class="favorites-board">
        <div class="board-header">
          <h2 class="board-title">Избранное</h2>
          <div class="pushpin"></div>
        </div>
        
        <div v-if="favoriteMaterials.length === 0" class="empty-board">
          <div class="empty-note">
            <div class="note-tape"></div>
            <p>Пока ничего не добавлено...</p>
            <button @click="currentPage = 'explore'" class="back-button-sketch">
              Перейти к материалам
            </button>
          </div>
        </div>
        
        <div v-else class="favorites-grid-board">
          <div 
            v-for="(material, index) in sortedFavorites" 
            :key="material.id"
            class="favorite-note"
            @click="selectMaterial(material)"
            :style="{ transform: `rotate(${getRandomRotation(index, 2)}deg)` }"
          >
            <div class="note-tape"></div>
            <div class="note-content">
              <div class="note-type" :class="getTypeClass(material.type)">{{ material.type }}</div>
              <h4 class="note-title" v-html="parseMarkdownV2(material.text.split('\n')[0])"></h4>
              <p class="note-desc" v-html="parseMarkdownV2(material.text.slice(0, 100) + (material.text.length > 100 ? '...' : ''))"></p>
              <div class="note-stats">
                <span>{{ getDate(material.created_at) }}</span>
                <span>⭐ {{ material.rating? material.rating : "-" }}/5</span>
              </div>
            </div>
            <button @click.stop="toggleFavorite(material.id)" class="remove-pin">×</button>
          </div>
        </div>
      </div>

      <!-- Страница прогресса в стиле инфографики -->
      <div v-if="currentPage === 'progress'" class="progress-infographic">
        <div class="infographic-header">
          <h2 class="infographic-title">Мой прогресс</h2>
          <div class="overall-progress-sketch">
            <div class="progress-circle">
              <svg viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="40" fill="none" stroke="#eee" stroke-width="8"/>
                <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" stroke-width="8"
                        :stroke-dasharray="`${getOverallProgress() * 2.51} 251`"
                        transform="rotate(-90 50 50)"/>
              </svg>
              <div class="progress-text">{{ getOverallProgress() }}%</div>
            </div>
          </div>
        </div>
        
        <!-- Прогресс по категориям в виде графиков -->
        <div class="categories-progress-sketch">
          <h3 class="section-title-sketch">По разделам:</h3>
          <div class="progress-bars-sketch">
            <div v-for="category in categories.slice(1)" :key="category.id" class="progress-bar-sketch">
              <div class="bar-label">{{ category.name }}</div>
              <div class="bar-container">
                <div class="bar-fill" :style="{ width: getCategoryProgress(category.text) + '%' }"></div>
                <span class="bar-percent">{{ getCategoryProgress(category.text) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Страница профиля -->
      <div v-if="currentPage === 'profile'" class="profile-sketch">
        <div class="profile-note">
          <div class="note-tape"></div>
          <h3>Профиль</h3>
          <p>Страница в разработке...</p>
        </div>
      </div>

      <!-- Нижняя навигация в стиле скетча -->
      <div class="bottom-nav-sketch">
        <div class="nav-container-sketch">
          <button 
            v-for="navItem in navigation" 
            :key="navItem.id"
            @click="currentPage = navItem.id"
            :class="['nav-button-sketch', currentPage === navItem.id ? 'nav-active-sketch' : '']"
          >
            <div class="nav-icon-sketch">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="navItem.icon" />
              </svg>
            </div>
            <span class="nav-label-sketch">{{ navItem.name }}</span>
            <div v-if="currentPage === navItem.id" class="nav-underline-sketch"></div>
          </button>
        </div>
      </div>

      <!-- Модальное окно в стиле журнальной статьи -->
      <div v-if="selectedMaterialData" class="modal-overlay-sketch" @click="selectedMaterialData = null">
        <div class="modal-magazine" @click.stop>
          <!-- Заголовок статьи -->
          <div class="magazine-header">
            <!--<div class="magazine-date">ПОТОК</div>-->
            <h2 class="magazine-title" v-html="parseMarkdownV2(selectedMaterialData.description)"></h2>
            <div class="magazine-type" :class="getTypeClass(selectedMaterialData.type)">
              {{ selectedMaterialData.type }}
            </div>
            <button @click="selectedMaterialData = null" class="close-sketch">×</button>
          </div>
          
          <!-- Содержимое статьи -->
          <div class="magazine-content">
            <div class="magazine-text">
              <p v-html="parseMarkdownV2(selectedMaterialData.text)"></p>
            </div>
            
            <!-- Информационные блоки -->
            <div class="info-blocks-sketch">
              <div class="info-block" :hidden="selectedMaterialData.category? false : true">
                <div class="block-title">Раздел:</div>
                <div class="block-value">{{ getCategoryName(selectedMaterialData.category) }}</div>
              </div>
              <div class="info-block">
                <div class="block-title">Дата:</div>
                <div class="block-value">{{ getDate(selectedMaterialData.created_at) }}</div>
              </div>
              <div class="info-block">
                <div class="block-title">Рейтинг:</div>
                <div class="block-value">⭐ {{ selectedMaterialData.rating? selectedMaterialData.rating : "- " }}/5</div>
              </div>
            </div>
            
            <!-- Теги в виде стикеров -->
            <div class="tags-stickers">
              <span v-for="tagId in selectedMaterialData.tags" :key="tagId" class="tag-sticker">
                {{ getTagName(tagId) }}
              </span>
            </div>
            
            <!-- Кнопки действий -->
            <div class="action-buttons-sketch">
              <button class="primary-button-sketch" @click="openLink()">
                {{ selectedMaterialData.type === 'Видео' ? 'Смотреть' : selectedMaterialData.type === 'Подкаст' ? 'Слушать' : 'Читать' }}
              </button>
              <div class="secondary-buttons-sketch">
                <button 
                  @click="toggleFavorite(selectedMaterialData.id)"
                  :class="['secondary-button-sketch', selectedMaterialData.isFavorite ? 'button-active' : '']"
                >
                  {{ selectedMaterialData.isFavorite ? '❤️ В избранном' : '🤍 В избранное' }}
                </button>
                <button 
                  @click="toggleCompleted(selectedMaterialData.id)"
                  :class="['secondary-button-sketch', selectedMaterialData.completed ? 'button-active' : '']"
                >
                  {{ selectedMaterialData.completed ? '✅ Пройдено' : '🔳 Отметить' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'

const searchQuery = ref('')
const selectedCategory = ref({ id: 'all', name: 'Все' })
const selectedTag = ref('all')
const currentPage = ref('explore')
//const favoriteSortBy = ref('recent')
const selectedMaterialData = ref(null)
const isDarkTheme = ref(false)

// Функция переключения темы
const toggleTheme = () => {
  isDarkTheme.value = !isDarkTheme.value
  localStorage.setItem('theme', isDarkTheme.value ? 'dark' : 'light')
}

// Загрузка сохраненной темы при монтировании
onMounted(() => {
  const savedTheme = localStorage.getItem('theme')
  if (savedTheme) {
    isDarkTheme.value = savedTheme === 'dark'
  } else {
    // Определяем системную тему
    isDarkTheme.value = window.matchMedia('(prefers-color-scheme: dark)').matches
  }
})

// Категории (разделы)
const categories = [
  { text: 'all', name: 'Все' },
  { text: 'команда', name: '#команда', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
  { text: 'продажи', name: '#продажи', icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4' },
  { text: 'маркетинг', name: '#маркетинг', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'ИИ', name: '#ИИ', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'стратегия', name: '#стратегия', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  { text: 'безопасность', name: '#безопасность', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'продукт', name: '#продукт', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'мышление', name: '#мышление', icon:'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'  },
  { text: 'лайф', name: '#лайф', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'разбор', name: '#разбор', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'Дашкиев', name: '#Дашкиев', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'Калашников', name: '#Калашников', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'эксперт', name: '#эксперт', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' }
]

// Теги
const tags = [
  { id: 'all', name: 'Все' },
  { id: 'text', name: 'Текст' },
  { id: 'video', name: 'Видео' },
  { id: 'podcast', name: 'Подкасты' }
]

// Навигация
const navigation = [
  { id: 'explore', name: 'Курсы', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
  { id: 'favorites', name: 'Избранное', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
  { id: 'progress', name: 'Прогресс', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
  //{ id: 'profile', name: 'Профиль', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' }
]

// Учебные материалы
/*const materials = ref([
  {
    id: 1,
    name: 'Основы JavaScript',
    description: 'Введение в JavaScript для начинающих разработчиков',
    fullDescription: 'Полный курс по основам JavaScript для начинающих разработчиков. Вы изучите синтаксис языка, работу с переменными, функциями, объектами и массивами. Курс включает практические задания и проекты для закрепления материала.',
    category: 'programming',
    type: 'Видео',
    tags: ['beginner', 'frontend'],
    duration: '2ч 30мин',
    rating: 4.7,
    isFavorite: false,
    completed: false,
    lastUpdated: '2 дня назад',
    completedDate: null
  },
  {
    id: 2,
    name: 'Продвинутый CSS и SASS',
    description: 'Изучение продвинутых техник CSS и препроцессора SASS',
    fullDescription: 'Курс для тех, кто уже знаком с основами CSS и хочет углубить свои знания. Вы изучите продвинутые селекторы, анимации, флексбокс, гриды и препроцессор SASS для оптимизации вашего кода.',
    category: 'programming',
    type: 'Статья',
    tags: ['advanced', 'frontend'],
    duration: '45мин',
    rating: 4.9,
    isFavorite: true,
    completed: true,
    lastUpdated: '1 неделю назад',
    completedDate: '3 дня назад'
  },
  {
    id: 3,
    name: 'Принципы UX дизайна',
    description: 'Основные принципы создания удобных интерфейсов',
    fullDescription: 'Подробный курс о принципах UX дизайна и создании удобных пользовательских интерфейсов. Вы узнаете о психологии пользователей, проведении исследований, создании прототипов и тестировании интерфейсов.',
    category: 'design',
    type: 'Видео',
    tags: ['beginner', 'ux'],
    duration: '3ч 15мин',
    rating: 4.5,
    isFavorite: false,
    completed: false,
    lastUpdated: '3 дня назад',
    completedDate: null
  },
  {
    id: 4,
    name: 'Разработка на React Native',
    description: 'Создание мобильных приложений с React Native',
    fullDescription: 'Практический курс по разработке кроссплатформенных мобильных приложений с использованием React Native. Вы научитесь создавать компоненты, работать с навигацией, хранить данные и взаимодействовать с API.',
    category: 'programming',
    type: 'Подкаст',
    tags: ['advanced', 'mobile'],
    duration: '1ч 20мин',
    rating: 4.8,
    isFavorite: true,
    completed: true,
    lastUpdated: '5 дней назад',
    completedDate: '1 день назад'
  },
  {
    id: 5,
    name: 'Основы Node.js',
    description: 'Серверная разработка на JavaScript с Node.js',
    fullDescription: 'Введение в серверную разработку с использованием Node.js. Вы изучите основы работы с файловой системой, создание HTTP-серверов, работу с базами данных и асинхронное программирование.',
    category: 'programming',
    type: 'Видео',
    tags: ['beginner', 'backend'],
    duration: '4ч 10мин',
    rating: 4.6,
    isFavorite: false,
    completed: false,
    lastUpdated: '1 день назад',
    completedDate: null
  },
  {
    id: 6,
    name: 'Figma для дизайнеров',
    description: 'Полное руководство по работе с Figma',
    fullDescription: 'Подробный курс по работе с Figma для дизайнеров интерфейсов. Вы научитесь создавать макеты, работать с компонентами, прототипировать и подготавливать дизайн для разработчиков.',
    category: 'design',
    type: 'Статья',
    tags: ['beginner', 'ux'],
    duration: '1ч 30мин',
    rating: 4.9,
    isFavorite: true,
    completed: false,
    lastUpdated: '4 дня назад',
    completedDate: null
  }
])*/

const materials = ref([])
onMounted(async () => {
  try {
    const favorites = localStorage.getItem('favorites') || '[]'
    const completed = localStorage.getItem('completed') || '[]'
    const request = await axios.get(`https://ncht.bz/api/tg/client/get-posts`, {
      params: {
        chatId: '"-1002586005268"'
        //chatId: '-1001919427599' // ID чата должен быть передан
      }
    })
    materials.value = request.data.posts
    materials.value.forEach(post => {
      post.isFavorite = JSON.parse(favorites).includes(post.id)
      post.completed = JSON.parse(completed).includes(post.id)
    })
  } catch (error) {
    console.error('Ошибка при получении постов:', error)
    materials.value = []
  }
})

// Вычисляемые свойства
const favoriteMaterials = computed(() => {
  return materials.value.filter(material => material.isFavorite)
})

const filteredMaterials = computed(() => {
  let filtered = materials.value
  if (selectedCategory.value.name !== 'Все') {
    filtered = filtered.filter(material => material.categories.includes(selectedCategory.value.name.slice(1)))
  }

  if (selectedTag.value !== 'all') {
    filtered = filtered.filter(material => material.tag && material.tag == selectedTag.value)
  }

  if (searchQuery.value.trim()) {
    console.log(searchQuery.value)
    const query = searchQuery.value?.toLowerCase() || ''
    filtered = filtered.filter(material => 
      material.no_formatted.message.toLowerCase().includes(query)
    )
  }

  return filtered
})

const sortedFavorites = computed(() => {
  const favorites = [...favoriteMaterials.value]
  return favorites.reverse()
})

// Методы
const selectMaterial = (material) => {
  selectedMaterialData.value = material
}

const toggleFavorite = (id) => {
  const material = materials.value.find(m => m.id === id)
  if (material) {
    material.isFavorite = !material.isFavorite
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]')
    if (material.isFavorite) {
      localStorage.setItem('favorites', JSON.stringify([...favorites, id]))
    } else {
      localStorage.setItem('favorites', JSON.stringify(favorites.filter(id => id !== id)))
    }
  }
}

const toggleCompleted = (id) => {
  const material = materials.value.find(m => m.id === id)
  if (material) {
    material.completed = !material.completed
    const completed = JSON.parse(localStorage.getItem('completed') || '[]')
    if (material.completed) {
      localStorage.setItem('completed', JSON.stringify([...completed, id]))
    } else {
      localStorage.setItem('completed', JSON.stringify(completed.filter(id => id !== id)))
    }
  }
}

const getDate = (date) => {
  return new Date(date * 1000).toLocaleDateString('ru-RU', {day: '2-digit', month: '2-digit', year: '2-digit'})
}

const openLink = () => {
  window.location.href = selectedMaterialData.value.tg_link;
}

const getTypeClass = (type) => {
  switch (type) {
    case 'Видео': return 'type-video'
    case 'Статья': return 'type-article'
    case 'Подкаст': return 'type-podcast'
    default: return 'type-unknown'
  }
}
const parseMarkdownV2 = (text) => {
  if (!text) return ''
  
  return text
    // Экранируем спецсимволы
    //.replace(/([_*[\]()~`>#+\-=|{}.!])/g, '\\$1')
    // Жирный текст
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Курсив
    .replace(/\*(.*?)\*/g, '<em>$1</em>') 
    .replace(/_(.*?)_/g, '<em>$1</em>')
    // Подчеркнутый
    .replace(/__(.*?)__/g, '<u>$1</u>')
    // Зачеркнутый
    .replace(/~~(.*?)~~/g, '<s>$1</s>')
    // Моноширинный
    .replace(/`(.*?)`/g, '<code>$1</code>')
    // Ссылки
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
    // Перенос строки
    .replace(/\n/g, '<br>')
}

const getCategoryName = (categoryId) => {
  const category = categories.find(c => c.text === categoryId)
  return category ? category.name : 'Неизвестно'
}

const getTagName = (tagId) => {
  const tag = tags.find(t => t.id === tagId)
  return tag ? tag.name : 'Неизвестно'
}

const getCategoryProgress = (category) => {
  if (category === 'all' || category?.name === 'Все') {
    return getOverallProgress()
  }
  
  const categoryName = typeof category === 'string' ? category : category?.name?.slice(1)
  const categoryMaterials = materials.value.filter(m => m.categories.includes(categoryName))
  if (categoryMaterials.length === 0) return 0
  
  const completedCount = categoryMaterials.filter(m => m.completed).length
  return Math.round((completedCount / categoryMaterials.length) * 100)
}

const getOverallProgress = () => {
  if (materials.value.length === 0) return 0
  
  const completedCount = materials.value.filter(m => m.completed).length
  return Math.round((completedCount / materials.value.length) * 100)
}

const getRandomRotation = (index, seed = 0) => {
  const rotations = [-2, -1, 0, 1, 2, -1.5, 1.5]
  return rotations[(index + seed) % rotations.length]
}
</script>

<style>
body {
  margin: 0px;
}
</style>

<style scoped>
/* Базовые стили */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* Шрифты в стиле рукописного текста */
@import url('https://fonts.googleapis.com/css2?family=Kalam:wght@300;400;700&family=Caveat:wght@400;500;600;700&display=swap');

/* Контейнер приложения */
.app-container {
  min-height: 100vh;
  background: #f8f6f0;
  color: #2d2d2d;
  font-family: 'Kalam', cursive;
  position: relative;
  transition: all 0.3s ease;
}

/* Темная тема */
.app-container.dark-theme {
  background: #1a1a1a;
  color: #e0e0e0;
}

/* Фоновая текстура */
.app-container::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(120, 219, 255, 0.03) 0%, transparent 50%);
  pointer-events: none;
}

.dark-theme::before {
  background-image: 
    radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(120, 219, 255, 0.08) 0%, transparent 50%);
}

/* Кнопка смены темы */
.theme-toggle-sketch {
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 100;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.75rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  transform: rotate(-2deg);
}

.theme-toggle-sketch:hover {
  transform: rotate(0deg);
  background: rgba(255, 255, 255, 0.1);
}

.dark-theme .theme-toggle-sketch:hover {
  background: rgba(255, 255, 255, 0.05);
}

.toggle-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.sun-icon, .moon-icon {
  width: 20px;
  height: 20px;
  color: #999;
  transition: all 0.3s ease;
}

.sun-icon.active {
  color: #ffa726;
  transform: scale(1.1);
}

.moon-icon.active {
  color: #42a5f5;
  transform: scale(1.1);
}

.toggle-divider {
  font-family: 'Caveat', cursive;
  font-size: 1.2rem;
  color: #999;
  transform: rotate(15deg);
}

.toggle-label {
  font-family: 'Caveat', cursive;
  font-size: 0.8rem;
  color: #666;
  text-align: center;
  margin-bottom: 0.25rem;
}

.dark-theme .toggle-label {
  color: #999;
}

.toggle-arrow {
  width: 20px;
  height: 15px;
  color: #ccc;
  margin: 0 auto;
}

/* Контейнер */
.tg-app-container {
  max-width: 28rem;
  margin: 0 auto;
  padding: 1.5rem 1rem;
  position: relative;
  z-index: 1;
}

/* Шапка в стиле скетча */
.header {
  margin-bottom: 2rem;
  text-align: center;
}

.header-sketch {
  position: relative;
  padding: 1rem;
}

.month-badge {
  display: inline-block;
  background: #2d2d2d;
  color: white;
  padding: 0.25rem 0.75rem;
  font-family: 'Caveat', cursive;
  font-weight: 700;
  font-size: 1rem;
  transform: rotate(-2deg);
  margin-bottom: 0.5rem;
}

.dark-theme .month-badge {
  background: #e0e0e0;
  color: #1a1a1a;
}

.main-title {
  font-family: 'Caveat', cursive;
  font-size: 2rem;
  font-weight: 700;
  color: #2d2d2d;
  margin-bottom: 0.5rem;
  transform: rotate(1deg);
}

.dark-theme .main-title {
  color: #e0e0e0;
}

.subtitle-arrow {
  position: relative;
  margin-top: 1rem;
}

.arrow-sketch {
  width: 120px;
  height: 30px;
  color: #666;
  margin-bottom: 0.5rem;
}

.dark-theme .arrow-sketch {
  color: #999;
}

.subtitle-text {
  font-size: 0.9rem;
  color: #666;
  font-style: italic;
}

.dark-theme .subtitle-text {
  color: #999;
}

.sketch-elements {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  gap: 1rem;
}

.brain-sketch, .lightbulb-sketch {
  width: 40px;
  height: 40px;
  color: #999;
}

.dark-theme .brain-sketch,
.dark-theme .lightbulb-sketch {
  color: #666;
}

/* Поиск в рукописном стиле */
.search-container {
  margin-bottom: 2rem;
}

.search-sketch-wrapper {
  position: relative;
}

.search-label {
  font-family: 'Caveat', cursive;
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 0.5rem;
  transform: rotate(-1deg);
}

.dark-theme .search-label {
  color: #999;
}

.search-input-wrapper {
  position: relative;
}

.search-input-sketch {
  width: 100%;
  background: transparent;
  border: none;
  padding: 0.75rem 0;
  font-family: 'Kalam', cursive;
  font-size: 1rem;
  color: #2d2d2d;
  outline: none;
}

.dark-theme .search-input-sketch {
  color: #e0e0e0;
}

.search-input-sketch::placeholder {
  color: #999;
  font-style: italic;
}

.dark-theme .search-input-sketch::placeholder {
  color: #666;
}

.search-underline {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: #2d2d2d;
  transform: rotate(-0.5deg);
}

.dark-theme .search-underline {
  background: #e0e0e0;
}

/* Разделы в стиле коллажа */
.categories-collage {
  margin-bottom: 2rem;
}

.collage-title {
  position: relative;
  margin-bottom: 1.5rem;
  text-align: center;
}

.handwritten-text {
  font-family: 'Caveat', cursive;
  font-size: 1.3rem;
  color: #2d2d2d;
  font-weight: 600;
}

.dark-theme .handwritten-text {
  color: #e0e0e0;
}

.underline-sketch {
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 200px;
  height: 10px;
  color: #666;
}

.dark-theme .underline-sketch {
  color: #999;
}

.categories-grid-sketch {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.category-card-sketch {
  position: relative;
  background: white;
  border-radius: 8px;
  padding: 1.5rem 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.dark-theme .category-card-sketch {
  background: #2d2d2d;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
}

.category-card-sketch:hover {
  transform: rotate(0deg) !important;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}

.dark-theme .category-card-sketch:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.4);
}

.category-active-sketch {
  background: #fff8e1;
  box-shadow: 0 4px 16px rgba(255, 193, 7, 0.2);
}

.dark-theme .category-active-sketch {
  background: #3d3d00;
  box-shadow: 0 4px 16px rgba(255, 193, 7, 0.3);
}

.sketch-frame {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.frame-border {
  width: 100%;
  height: 100%;
  color: #ddd;
}

.dark-theme .frame-border {
  color: #555;
}

.category-content-sketch {
  position: relative;
  z-index: 2;
  text-align: center;
}

.category-icon-sketch {
  width: 3rem;
  height: 3rem;
  margin: 0 auto 0.75rem;
  background: #f5f5f5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dark-theme .category-icon-sketch {
  background: #1a1a1a;
}

.category-icon {
  width: 1.5rem;
  height: 1.5rem;
  color: #666;
}

.dark-theme .category-icon {
  color: #999;
}

.category-name-handwritten {
  font-family: 'Caveat', cursive;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d2d2d;
  margin-bottom: 0.75rem;
}

.dark-theme .category-name-handwritten {
  color: #e0e0e0;
}

.progress-sketch {
  text-align: center;
}

.progress-label {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.25rem;
}

.dark-theme .progress-label {
  color: #999;
}

.progress-line {
  width: 100px;
  height: 8px;
  color: #4caf50;
}

.category-sticker {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background: #ff6b6b;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: bold;
  transform: rotate(15deg);
}

/* Фильтры в стиле заметок */
.tags-notes {
  margin-bottom: 2rem;
}

.notes-title {
  font-family: 'Caveat', cursive;
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 1rem;
  transform: rotate(-1deg);
}

.dark-theme .notes-title {
  color: #999;
}

.tag-notes-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.tag-note {
  position: relative;
  background: #fff3cd;
  border: none;
  padding: 0.5rem 1rem;
  font-family: 'Kalam', cursive;
  font-size: 0.9rem;
  color: #856404;
  cursor: pointer;
  transition: all 0.3s ease;
  transform: rotate(-1deg);
}

.dark-theme .tag-note {
  background: #4a4a00;
  color: #ffeb3b;
}

.tag-note:hover {
  transform: rotate(0deg);
  background: #ffeaa7;
}

.dark-theme .tag-note:hover {
  background: #5a5a00;
}

.tag-note-active {
  background: #fd79a8;
  color: white;
  transform: rotate(2deg);
}

.dark-theme .tag-note-active {
  background: #c2185b;
}

.note-corner {
  position: absolute;
  top: 0;
  right: 0;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-top: 8px solid #ddd;
}

.dark-theme .note-corner {
  border-top-color: #555;
}

/* Материалы в стиле журнала */
.materials-magazine {
  margin-bottom: 6rem;
}

.material-article {
  position: relative;
  background: white;
  margin-bottom: 2rem;
  padding: 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.dark-theme .material-article {
  background: #2d2d2d;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
}

.material-article:hover {
  transform: rotate(0deg) !important;
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
}

.dark-theme .material-article:hover {
  box-shadow: 0 6px 20px rgba(0,0,0,0.4);
}

.article-frame {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.article-border {
  width: 100%;
  height: 100%;
  color: #eee;
}

.dark-theme .article-border {
  color: #555;
}

.article-content {
  position: relative;
  z-index: 2;
}

.article-header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.article-type-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.type-video {
  background: #e3f2fd;
  color: #1976d2;
}

.dark-theme .type-video {
  background: #0d47a1;
  color: #bbdefb;
}

.type-article {
  background: #e8f5e8;
  color: #388e3c;
}

.dark-theme .type-article {
  background: #1b5e20;
  color: #c8e6c9;
}

.type-podcast {
  background: #fff3e0;
  color: #f57c00;
}

.dark-theme .type-podcast {
  background: #e65100;
  color: #ffe0b2;
}

.article-title {
  flex: 1;
  font-family: 'Caveat', cursive;
  font-size: 1.3rem;
  font-weight: 600;
  color: #2d2d2d;
  line-height: 1.2;
}

.dark-theme .article-title {
  color: #e0e0e0;
}

.paperclip-favorite {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: #ccc;
  transition: all 0.3s ease;
}

.paperclip-favorite:hover {
  color: #666;
  transform: rotate(15deg);
}

.dark-theme .paperclip-favorite:hover {
  color: #999;
}

.paperclip-active {
  color: #ff6b6b;
  transform: rotate(15deg);
}

.paperclip-favorite svg {
  width: 20px;
  height: 30px;
}

.article-description {
  font-family: 'Kalam', cursive;
  color: #666;
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 1rem;
}

.dark-theme .article-description {
  color: #999;
}

.article-stats-sketch {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.85rem;
}

.stat-sketch {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #666;
}

.dark-theme .stat-sketch {
  color: #999;
}

.stat-icon {
  font-size: 1rem;
}

.checkbox-sketch {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  font-family: 'Kalam', cursive;
  font-size: 0.85rem;
  color: #666;
  transition: all 0.3s ease;
}

.dark-theme .checkbox-sketch {
  color: #999;
}

.checkbox-sketch:hover {
  color: #2d2d2d;
}

.dark-theme .checkbox-sketch:hover {
  color: #e0e0e0;
}

.checkbox-checked {
  color: #4caf50;
}

.checkbox-sketch svg {
  width: 20px;
  height: 20px;
}

.article-arrow {
  position: absolute;
  top: -15px;
  right: -15px;
  width: 50px;
  height: 30px;
  color: #ddd;
}

.dark-theme .article-arrow {
  color: #555;
}

/* Страница избранного в стиле доски объявлений */
.favorites-board {
  margin-bottom: 6rem;
}

.board-header {
  position: relative;
  text-align: center;
  margin-bottom: 2rem;
}

.board-title {
  font-family: 'Caveat', cursive;
  font-size: 2rem;
  font-weight: 700;
  color: #2d2d2d;
  transform: rotate(-1deg);
}

.dark-theme .board-title {
  color: #e0e0e0;
}

.pushpin {
  position: absolute;
  top: -10px;
  right: 50%;
  transform: translateX(50%);
  width: 12px;
  height: 12px;
  background: #ff6b6b;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.empty-board {
  text-align: center;
  padding: 3rem 1rem;
}

.empty-note {
  position: relative;
  background: #fff3cd;
  padding: 2rem;
  border-radius: 4px;
  transform: rotate(-2deg);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.dark-theme .empty-note {
  background: #4a4a00;
  color: #ffeb3b;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

.note-tape {
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 16px;
  background: rgba(255,255,255,0.8);
  border-radius: 2px;
}

.dark-theme .note-tape {
  background: rgba(255,255,255,0.2);
}

.back-button-sketch {
  background: #2d2d2d;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-family: 'Kalam', cursive;
  font-size: 0.9rem;
  cursor: pointer;
  margin-top: 1rem;
  transform: rotate(1deg);
  transition: all 0.3s ease;
}

.dark-theme .back-button-sketch {
  background: #e0e0e0;
  color: #1a1a1a;
}

.back-button-sketch:hover {
  transform: rotate(0deg);
  background: #1a1a1a;
}

.dark-theme .back-button-sketch:hover {
  background: #f5f5f5;
}

.favorites-grid-board {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.favorite-note {
  position: relative;
  background: #fff;
  padding: 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.dark-theme .favorite-note {
  background: #2d2d2d;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
}

.favorite-note:hover {
  transform: rotate(0deg) !important;
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
}

.dark-theme .favorite-note:hover {
  box-shadow: 0 6px 20px rgba(0,0,0,0.4);
}

.note-content {
  position: relative;
  z-index: 2;
}

.note-type {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
}

.note-title {
  font-family: 'Caveat', cursive;
  font-size: 1.2rem;
  font-weight: 600;
  color: #2d2d2d;
  margin-bottom: 0.5rem;
}

.dark-theme .note-title {
  color: #e0e0e0;
}

.note-desc {
  font-size: 0.85rem;
  color: #666;
  line-height: 1.3;
  margin-bottom: 0.75rem;
}

.dark-theme .note-desc {
  color: #999;
}

.note-stats {
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  color: #999;
}

.dark-theme .note-stats {
  color: #666;
}

.remove-pin {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #ff6b6b;
  color: white;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  transition: all 0.3s ease;
}

.remove-pin:hover {
  background: #e55555;
  transform: rotate(90deg);
}

/* Страница прогресса в стиле инфографики */
.progress-infographic {
  margin-bottom: 6rem;
}

.infographic-header {
  text-align: center;
  margin-bottom: 2rem;
}

.infographic-title {
  font-family: 'Caveat', cursive;
  font-size: 2rem;
  font-weight: 700;
  color: #2d2d2d;
  margin-bottom: 1rem;
  transform: rotate(-1deg);
}

.dark-theme .infographic-title {
  color: #e0e0e0;
}

.overall-progress-sketch {
  display: flex;
  justify-content: center;
}

.progress-circle {
  position: relative;
  width: 120px;
  height: 120px;
}

.progress-circle svg {
  width: 100%;
  height: 100%;
  color: #4caf50;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-family: 'Caveat', cursive;
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d2d2d;
}

.dark-theme .progress-text {
  color: #e0e0e0;
}

.categories-progress-sketch {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transform: rotate(-0.5deg);
}

.dark-theme .categories-progress-sketch {
  background: #2d2d2d;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
}

.section-title-sketch {
  font-family: 'Caveat', cursive;
  font-size: 1.3rem;
  font-weight: 600;
  color: #2d2d2d;
  margin-bottom: 1rem;
}

.dark-theme .section-title-sketch {
  color: #e0e0e0;
}

.progress-bars-sketch {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.progress-bar-sketch {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.bar-label {
  min-width: 120px;
  font-size: 0.9rem;
  color: #666;
}

.dark-theme .bar-label {
  color: #999;
}

.bar-container {
  flex: 1;
  position: relative;
  height: 20px;
  background: #f0f0f0;
  border-radius: 10px;
  overflow: hidden;
}

.dark-theme .bar-container {
  background: #1a1a1a;
}

.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #8bc34a);
  border-radius: 10px;
  transition: width 0.5s ease;
}

.bar-percent {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8rem;
  color: #666;
}

.dark-theme .bar-percent {
  color: #999;
}

/* Страница профиля */
.profile-sketch {
  margin-bottom: 6rem;
  display: flex;
  justify-content: center;
}

.profile-note {
  position: relative;
  background: #fff3cd;
  padding: 2rem;
  border-radius: 4px;
  transform: rotate(-2deg);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  text-align: center;
}

.dark-theme .profile-note {
  background: #4a4a00;
  color: #ffeb3b;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

/* Нижняя навигация в стиле скетча */
.bottom-nav-sketch {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-top: 2px solid #eee;
  z-index: 10;
}

.dark-theme .bottom-nav-sketch {
  background: rgba(26, 26, 26, 0.95);
  border-top-color: #555;
}

.nav-container-sketch {
  display: flex;
  justify-content: space-around;
  padding: 0.75rem 0;
  max-width: 28rem;
  margin: 0 auto;
}

.nav-button-sketch {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  transition: all 0.3s ease;
  color: #999;
}

.nav-active-sketch {
  color: #2d2d2d;
}

.dark-theme .nav-active-sketch {
  color: #e0e0e0;
}

.nav-icon-sketch {
  width: 24px;
  height: 24px;
  margin-bottom: 0.25rem;
}

.nav-label-sketch {
  font-family: 'Kalam', cursive;
  font-size: 0.75rem;
}

.nav-underline-sketch {
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 2px;
  background: #2d2d2d;
  border-radius: 1px;
}

.dark-theme .nav-underline-sketch {
  background: #e0e0e0;
}

/* Модальное окно в стиле журнальной статьи */
.modal-overlay-sketch {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-magazine {
  background: white;
  width: 100%;
  max-width: 28rem;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 8px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.2);
  /*transform: rotate(-0.5deg);*/
}

.dark-theme .modal-magazine {
  background: #2d2d2d;
  box-shadow: 0 20px 40px rgba(0,0,0,0.4);
}

.magazine-header {
  background: url('@/assets/images/magazine-header-bg.png') no-repeat center center !important;
  background-size: cover !important;
  position: relative;
  /*background: #f8f8f8 !important;*/
  padding: 1.5rem;
  border-bottom: 2px solid #eee;
  text-align: center;
  height: 300px;
}

.dark-theme .magazine-header {
  background: #1a1a1a;
  border-bottom-color: #555;
}

.magazine-date {
  background: #2d2d2d;
  color: white;
  padding: 0.25rem 0.75rem;
  font-family: 'Caveat', cursive;
  font-weight: 700;
  font-size: 0.9rem;
  display: inline-block;
  transform: rotate(-2deg);
  margin-bottom: 0.5rem;
}

.dark-theme .magazine-date {
  background: #e0e0e0;
  color: #1a1a1a;
}

.magazine-title {
  font-family: 'Caveat', cursive;
  font-size: 1.5rem;
  font-weight: 700;
  color: #e0e0e0;
  margin-bottom: 0.5rem;
  background-color: antiquewhite;
  border-radius: 1rem;
}

.dark-theme .magazine-title {
  color: #2d2d2d;
}

.magazine-type {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.close-sketch {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #999;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.close-sketch:hover {
  background: #f0f0f0;
  color: #2d2d2d;
}

.dark-theme .close-sketch:hover {
  background: #555;
  color: #e0e0e0;
}

.magazine-content {
  padding: 1.5rem;
}

.magazine-text {
  margin-bottom: 1.5rem;
  line-height: 1.6;
  color: #444;
}

.dark-theme .magazine-text {
  color: #ccc;
}

.info-blocks-sketch {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.info-block {
  background: #f8f8f8;
  padding: 1rem;
  border-radius: 4px;
  text-align: center;
  transform: rotate(-1deg);
}

.dark-theme .info-block {
  background: #1a1a1a;
}

.info-block:nth-child(even) {
  transform: rotate(1deg);
}

.block-title {
  font-size: 0.75rem;
  color: #999;
  text-transform: uppercase;
  margin-bottom: 0.25rem;
}

.dark-theme .block-title {
  color: #666;
}

.block-value {
  font-family: 'Caveat', cursive;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d2d2d;
}

.dark-theme .block-value {
  color: #e0e0e0;
}

.tags-stickers {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.tag-sticker {
  background: #fff3cd;
  color: #856404;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-family: 'Kalam', cursive;
  transform: rotate(-2deg);
}

.dark-theme .tag-sticker {
  background: #4a4a00;
  color: #ffeb3b;
}

.tag-sticker:nth-child(even) {
  transform: rotate(2deg);
}

.action-buttons-sketch {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.primary-button-sketch {
  background: #2d2d2d;
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 4px;
  font-family: 'Kalam', cursive;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transform: rotate(-1deg);
  transition: all 0.3s ease;
}

.dark-theme .primary-button-sketch {
  background: #e0e0e0;
  color: #1a1a1a;
}

.primary-button-sketch:hover {
  transform: rotate(0deg);
  background: #1a1a1a;
}

.dark-theme .primary-button-sketch:hover {
  background: #f5f5f5;
}

.secondary-buttons-sketch {
  display: flex;
  gap: 0.75rem;
}

.secondary-button-sketch {
  flex: 1;
  background: #f8f8f8;
  border: 1px solid #ddd;
  color: #666;
  padding: 0.75rem;
  border-radius: 4px;
  font-family: 'Kalam', cursive;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  transform: rotate(1deg);
}

.dark-theme .secondary-button-sketch {
  background: #1a1a1a;
  border-color: #555;
  color: #999;
}

.secondary-button-sketch:hover {
  transform: rotate(0deg);
  background: #f0f0f0;
  color: #2d2d2d;
}

.dark-theme .secondary-button-sketch:hover {
  background: #333;
  color: #e0e0e0;
}

.button-active {
  background: #e8f5e8;
  border-color: #4caf50;
  color: #2e7d32;
}

.dark-theme .button-active {
  background: #1b5e20;
  border-color: #4caf50;
  color: #c8e6c9;
}

.button-active:hover {
  background: #c8e6c9;
}

.dark-theme .button-active:hover {
  background: #2e7d32;
}

/* Адаптивные настройки */
@media (max-width: 640px) {
  .tg-app-container {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
  
  .categories-grid-sketch {
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
  
  .favorites-grid-board {
    grid-template-columns: 1fr;
  }
  
  .info-blocks-sketch {
    grid-template-columns: 1fr;
  }
  
  .secondary-buttons-sketch {
    flex-direction: column;
  }
  
  .theme-toggle-sketch {
    top: 0.5rem;
    left: 0.5rem;
    padding: 0.5rem;
  }
}

/* Анимации */
@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(-2deg); }
  50% { transform: translateY(-5px) rotate(-2deg); }
}

.category-card-sketch:hover {
  animation: float 2s ease-in-out infinite;
}

.material-article:hover {
  animation: float 2s ease-in-out infinite;
}

/* Дополнительные декоративные элементы */
.sketch-decoration {
  position: absolute;
  pointer-events: none;
  color: #eee;
}

.dark-theme .sketch-decoration {
  color: #555;
}

/* Эффекты для рукописного стиля */
.handwritten-effect {
  position: relative;
}

.handwritten-effect::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 1px;
  background: currentColor;
  opacity: 0.3;
  transform: rotate(-0.5deg);
}
</style>